import { Campaign, Faq, FaqTopic } from './index';

// Order Management Portal editor — shared enums, consts and interfaces.

export enum OmpSectionKey {
  Header = 'header',
  AnnouncementBar = 'announcement_bar',
  WelcomeMessage = 'welcome_message',
  NavigationalBlocks = 'navigational_blocks',
  Footer = 'footer',
  NextOrderDetails = 'next_order_details',
  MomentCard = 'moment_card',
  SubscriptionList = 'subscription_list',
  OrderList = 'order_list',
  OrderSummary = 'order_summary',
  CrossSellCarousel = 'cross_sell_carousel',
  CustomHtml = 'custom_html',
  Video = 'video',
  Callout = 'callout',
  TextAndImage = 'text_and_image',
  ImageBanner = 'image_banner',
  CreditsBalance = 'credits_balance',
  Referrals = 'referrals',
  Columns = 'columns',
  FaqSection = 'faq_section',
  Instagram = 'instagram',
  TextBox = 'text_box',
  ShippingAndPayment = 'shipping_and_payment',
}

export enum OmpPage {
  Home = 'home',
  Subscriptions = 'subscriptions',
  SubscriptionDetails = 'subscription_details',
  Orders = 'orders',
  OrderDetails = 'order_details',
  Account = 'account',
}

export enum OmpMoment {
  None = 0,
  OrderPending = 1,
  OrderShipped = 2,
  SubscriptionActive = 3,
  SubscriptionCancelled = 4,
  PaymentIssue = 5,
  Trial = 6,
}

export enum OmpSectionIssueKind {
  LabelLength = 'label_length',
  Required = 'required',
  RequiredAnyOf = 'required_any_of',
}

export interface OmpLabelIssue {
  page: OmpPage;
  moment: OmpMoment;
  sectionKey: string;
  sectionIndex: number;
  childIndex: number;
  field: string;
  kind: OmpSectionIssueKind;
  length: number;
}

export const OmpCtaRequiredFields = ['cta_label', 'cta_url'];

export const OmpRequiredSectionFields: { [key: string]: string[] } = {
  [OmpSectionKey.TextAndImage]: ['section_heading', 'image'],
  [OmpSectionKey.Callout]: ['section_heading'],
  [OmpSectionKey.Video]: ['video_url'],
  [OmpSectionKey.ImageBanner]: ['desktop_image'],
};

export const OmpRequiredAnyOfFields: { [key: string]: string[] } = {
  [OmpSectionKey.TextBox]: ['section_heading', 'description'],
};

export const OmpFieldLabels: { [field: string]: string } = {
  cta_label: 'Button Label',
  cta_url: 'Button Link',
  video_url: 'Video Link',
  desktop_image: 'Desktop Image',
  image: 'Image',
};

export const OmpSampleDataNotes: { [key: string]: string } = {
  [OmpSectionKey.ShippingAndPayment]:
    'The address and card shown in the preview are sample data. Each customer sees their own saved address and payment method.',
  [OmpSectionKey.OrderList]:
    'The orders shown in the preview are sample data. Each customer sees their own orders.',
  [OmpSectionKey.SubscriptionList]:
    'The subscriptions shown in the preview are sample data. Each customer sees their own subscriptions.',
  [OmpSectionKey.NextOrderDetails]:
    'The product, dates and prices shown in the preview are sample data. Each customer sees their own.',
  [OmpSectionKey.MomentCard]:
    'The product, dates and prices shown in the preview are sample data. Each customer sees their own.',
  [OmpSectionKey.OrderSummary]:
    'The items and totals shown in the preview are sample data. Each customer sees their own.',
  [OmpSectionKey.CrossSellCarousel]:
    'The products, prices and discounts shown in the preview are sample data. Each customer sees the products linked ' +
    "to their order or subscription. On Home's Default moment there is none, so the section does not show there.",
};

export const OMP_PREVIEW_SOURCE_HOST = 'solvpath-omp-host';
export const OMP_PREVIEW_SOURCE_PREVIEW = 'solvpath-omp-preview';

export enum OmpPreviewReason {
  Structure = 'structure',
  Data = 'data',
  Theme = 'theme',
}

export enum OmpHostMessageType {
  Hello = 'hello',
  Init = 'init',
  Config = 'config',
  ConfigPage = 'configPage',
  Theme = 'theme',
  Page = 'page',
  Moment = 'moment',
  Device = 'device',
  Select = 'select',
  Hover = 'hover',
  Faqs = 'faqs',
  Navigation = 'navigation',
}

export enum OmpPreviewMessageType {
  Ready = 'ready',
  Navigate = 'navigate',
  Select = 'select',
  AddRequest = 'addRequest',
  Reorder = 'reorder',
  DeleteRequest = 'deleteRequest',
  History = 'history',
}

export interface OmpQueuedMessage {
  type: OmpHostMessageType;
  payload: any;
}

export interface OmpPreviewSection {
  key: string;
  uid: string;
  name: string;
  is_visible: boolean;
  deletable: boolean;
  data: any;
}

export interface OmpPreviewCampaign
  extends Partial<Pick<Campaign, 'id' | 'name' | 'subdomain' | 'display_name' | 'support_email' | 'support_phone'>> {
  logo?: string;
  mobile_logo?: string;
}

export interface OmpHostInitPayload {
  subdomainType: number;
  campaign: OmpPreviewCampaign;
  theme: any;
  portalSettings: any;
  page: string;
  moment: string;
  device: string;
  selectedUid: string | null;
}

export interface OmpHostConfigPayload {
  portalSettings: any;
}

export interface OmpHostConfigPagePayload {
  page: string;
  moment: string;
  sections: OmpPreviewSection[];
}

export interface OmpHostThemePayload {
  theme: any;
}

export interface OmpHostPagePayload {
  page: string;
}

export interface OmpHostMomentPayload {
  moment: string;
}

export interface OmpHostDevicePayload {
  device: string;
}

export interface OmpHostSelectPayload {
  uid: string | null;
  reveal?: boolean;
}

export interface OmpHostHoverPayload {
  uid: string | null;
}

export interface OmpHostNavigationPayload {
  enabled: boolean;
}

export interface OmpPreviewFaqTopic extends Pick<FaqTopic, 'title'> {
  id: number;
}

export interface OmpPreviewFaq extends Pick<Faq, 'question' | 'answer'> {
  id: number;
}

export interface OmpHostFaqsPayload {
  topics: OmpPreviewFaqTopic[];
  faqs: OmpPreviewFaq[];
}

export interface OmpHostMessage {
  source: string;
  seq: number;
  type: OmpHostMessageType;
  payload: any;
}

export interface OmpPreviewNavigatePayload {
  page: string;
}

export interface OmpPreviewSelectPayload {
  page: string;
  moment: string;
  uid: string;
  sectionKey: string;
  index: number;
  childIndex?: number;
}

export interface OmpPreviewAddRequestPayload {
  page: string;
  moment: string;
  anchorUid: string;
  insert: string;
  zone: string;
}

export interface OmpPreviewReorderPayload {
  page: string;
  moment: string;
  order: string[];
  basedOnSeq: number;
}

export interface OmpPreviewDeleteRequestPayload {
  page: string;
  moment: string;
  uid: string;
}

export interface OmpPreviewMessage {
  source: string;
  seq: number;
  type: OmpPreviewMessageType;
  payload: any;
}

export enum OmpSection {
  PageHeader = 'page-header',
  ThemeColors = 'theme-colors',
  CardStyle = 'card-style',
  Images = 'images',
  WidthAndHeight = 'width-and-height',
  Typography = 'typography',
  Layout = 'layout',
  ButtonsAndInteractions = 'buttons-and-interactions',
}

export interface OmpSectionConfig {
  id: OmpSection;
  label: string;
  hasInfo: boolean;
  help?: string;
}

export enum OmpTab {
  Sections = 'sections',
  GlobalStyles = 'global-styles',
}

export enum OmpDevicePreview {
  Desktop = 'desktop',
  Tablet = 'tablet',
  Mobile = 'mobile',
  FullScreen = 'full-screen',
}

export enum OmpLogoPosition {
  TopLeft = 'top-left',
  TopCenter = 'top-center',
  TopRight = 'top-right',
}

export enum OmpAspectRatio {
  Square = 'square',
  Portrait = 'portrait',
  Landscape = 'landscape',
}

export enum OmpCornerRoundness {
  FullyRounded = 'fully-rounded',
  GentlyRounded = 'gently-rounded',
  Square = 'square',
}

export enum OmpBorderStyle {
  Solid = 'solid',
  Dashed = 'dashed',
  Dotted = 'dotted',
  None = 'none',
}

export enum OmpNavPosition {
  Left = 'left',
  Right = 'right',
  Top = 'top',
}

export enum OmpPopupLayout {
  Center = 'center',
  Right = 'right',
}

export enum OmpButtonPlacement {
  OrderControls = 'order-controls',
  PageBottom = 'page-bottom',
}

export enum OmpControlKey {
  ChangeVariant = 'change_variant',
  RevisePackage = 'revise_package',
  SwapProduct = 'swap_product',
  BuyAgain = 'buy_again',
  TrackPackage = 'track_package',
  ReturnProduct = 'return_product',
  CancelShipment = 'cancel_shipment',
  CancelSubscription = 'cancel_subscription',
  SendNow = 'send_now',
  Skip = 'skip',
  Pause = 'pause',
  Reschedule = 'reschedule',
  Reactivate = 'reactivate',
}

export enum OmpLegacyControlKey {
  AddProducts = 'add_products',
  Delay = 'delay',
  ResumeSubscription = 'resume_subscription',
  Swap = 'swap',
  TrackOrder = 'track_order',
}

export enum OmpMomentActionKey {
  SendNow = 'send_now',
  Reschedule = 'reschedule',
  Skip = 'skip',
  Swap = 'swap',
  BuyAgain = 'buy_again',
  TrackOrder = 'track_order',
  RebillItem = 'rebill_item',
  Reactivate = 'reactivate',
  ExtendTrial = 'extend_trial',
  AdjustTrialFrequency = 'adjust_trial_frequency',
  BillTrialNow = 'bill_trial_now',
  ReturnTrial = 'return_trial',
  CancelTrial = 'cancel_trial',
}

export interface OmpMomentAction {
  key: OmpMomentActionKey;
  is_visible: boolean;
  label: string;
  icon: string;
}

export interface OmpMomentActionDef {
  key: OmpMomentActionKey;
  name: string;
  help: string;
  label: string;
  icon: string;
}

export enum OmpIconStyle {
  Solid = 'solid',
  Regular = 'regular',
  Brands = 'brands',
}

export enum OmpButtonStyle {
  Fill = 'fill',
  LowFill = 'low-fill',
  Outline = 'out-line',
  Text = 'text',
}

export interface OmpPageSectionItem {
  key: string;
  data?: any;
}

export enum OmpSectionPosition {
  Top = 'top',
  Body = 'body',
  Bottom = 'bottom',
}

export enum OmpHeadingSize {
  Small = 'small',
  Medium = 'medium',
  Large = 'large',
}

export enum OmpHeadingAlignment {
  Left = 'left',
  Center = 'center',
}

export enum OmpFaqLayout {
  SingleColumn = 'single_column',
  DoubleColumn = 'double_column',
}

export enum OmpImageRatio {
  Adapt = 'adapt',
  SixteenNine = '16_9',
  FourThree = '4_3',
  Square = '1_1',
}

export enum OmpPillButtonStyle {
  LowFillPill = 'low_fill_pill',
  FillPill = 'fill_pill',
  OutlinePill = 'outline_pill',
  Text = 'text',
}

export enum OmpInstagramLayout {
  Collage = 'collage',
  EqualGrid = 'equal_grid',
  Carousel = 'carousel',
}

export enum OmpTextImageLayout {
  ImageLeft = 'image_left',
  ImageRight = 'image_right',
  ImageTop = 'image_top',
  ImageBottom = 'image_bottom',
}

export enum OmpImageFit {
  Contain = 'contain',
  Cover = 'cover',
}

export enum OmpContentAlignment {
  Top = 'top',
  Center = 'center',
  Bottom = 'bottom',
}

export enum OmpTextAlignment {
  Left = 'left',
  Center = 'center',
}

export enum OmpMobileLayout {
  ImageFirst = 'image_first',
  TextFirst = 'text_first',
}

export enum OmpCalloutLayout {
  Stacked = 'stacked',
  Inline = 'inline',
  Banner = 'banner',
}

export enum OmpCalloutTone {
  Brand = 'brand',
  Positive = 'positive',
  Caution = 'caution',
  Plain = 'plain',
}

export enum OmpCalloutIcon {
  None = 'none',
  Custom = 'custom',
  Sparkles = 'sparkles',
  Gift = 'gift',
  Percent = 'percent',
  Tag = 'tag',
  Truck = 'truck',
  Clock = 'clock',
  Shield = 'shield',
  Info = 'info',
  Alert = 'alert',
  Star = 'star',
  Heart = 'heart',
  Party = 'party',
}

export enum OmpAccentStyle {
  None = 'none',
  LeftBar = 'left_bar',
  TopBar = 'top_bar',
}

export enum OmpVideoAspect {
  SixteenNine = '16_9',
  FourThree = '4_3',
  Square = '1_1',
}

export enum OmpColumnsLayout {
  Equal = 'equal',
  WideLeft = 'wide_left',
  WideRight = 'wide_right',
}

export enum OmpColumnsVerticalAlign {
  Stretch = 'stretch',
  Top = 'top',
  Center = 'center',
}

export enum OmpImageControl {
  Logo = 'logo',
  PageBackground = 'page_background_image',
}

export enum OmpButtonGroup {
  Primary = 'primary_button',
  Secondary = 'secondary_button',
  Tertiary = 'tertiary_button',
}

export interface OmpSectionDef {
  key: string;
  name: string;
  description: string;
  position: OmpSectionPosition;
  locked: boolean;
  deletable: boolean;
  hideable?: boolean;
  hasDetailSettings: boolean;
  addable: boolean;
  repeatable?: boolean;
  defaultVisible: boolean;
  // Pages the picker offers this section on; omitted means every page.
  pages?: OmpPage[];
  // Only staff users see it in the picker while it is still being rolled out.
  adminOnly?: boolean;
}

export interface OmpImageSlot {
  contentWidth: number;
  horizontalPadding: number;
  columnCount?: number;
  share?: number;
}

export interface OmpPalette {
  theme: string;
  onTheme: string;
  hover: string;
  tintStrong: string;
  tintSoft: string;
  tintFaint: string;
  surface: string;
  text: string;
}

export enum OmpFieldKind {
  Color = 'color',
  Slider = 'slider',
  Radio = 'radio',
  Toggle = 'toggle',
  Checkbox = 'checkbox',
  Dropdown = 'dropdown',
  Image = 'image',
  Subheading = 'subheading',
}

export interface OmpStyleField {
  kind: OmpFieldKind;
  key?: string;
  label?: string;
  help?: string;
  eye?: boolean;
  unit?: string;
  min?: number;
  max?: number;
  options?: { value: string; label: string }[];
  checkboxes?: { key: string; label: string }[];
  hint?: string;
}

export interface OmpRuntimeSection {
  key: string;
  data: any;
  visible: boolean;
  uid?: string;
}

export interface OmpRevealTarget {
  page: OmpPage;
  moment: OmpMoment;
  uid: string;
  childIndex?: number;
}

export enum OmpHistoryAction {
  Undo = 'undo',
  Redo = 'redo',
}

export enum OmpHistoryLabel {
  LastSaved = 'Last saved',
  Reorder = 'Reorder sections',
  Sync = 'Sync sections',
  RestoreBrand = 'Restore brand defaults',
  Styles = 'Global styles',
  Sections = 'Sections',
  Add = 'Add',
  Delete = 'Delete',
  Hide = 'Hide',
  Show = 'Show',
}

export enum OmpHistoryVerb {
  Undid = 'Undid',
  Redid = 'Redid',
}

export const OMP_HISTORY_LIMIT = 10;
export const OMP_TEXT_GROUP_MS = 800;
export const OMP_HISTORY_TOAST_MS = 1800;

export interface OmpHistoryStep {
  label: string;
  styles: string;
  sections: string;
}
